import { createSignal } from "solid-js";

import { closestSelector } from "@/lib/gestures/guards";
import { applyRubberBand, createGestureRecognizer } from "@/lib/gestures/recognizer";
import { pointerHandlers } from "@/lib/gestures/solid";
import { STOCK_ROW_SWIPE } from "@/lib/gestures/presets";
import { createPrefersReducedMotion } from "@/lib/use-media-query";

const {
  thresholdPx: THRESHOLD_PX,
  commitThresholdPx: COMMIT_THRESHOLD_PX,
  rubberBandLimitPx: RUBBER_BAND_LIMIT,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  hapticMs: HAPTIC_MS,
  commitAnimationMs: COMMIT_ANIMATION_MS,
  cancelAnimationMs: CANCEL_ANIMATION_MS,
  fallbackWidthPx: FALLBACK_WIDTH_PX,
} = STOCK_ROW_SWIPE;

export interface SwipeRowAction {
  /** Handler fired when the swipe commits past the threshold. */
  onCommit: () => void;
  /** Whether committing slides the row fully off and removes it. */
  exits: boolean;
}

export interface SwipeRowOptions {
  /** Right-swipe action (revealed at the left edge), or undefined to disable. */
  getRight: () => SwipeRowAction | undefined;
  /** Left-swipe action (revealed at the right edge), or undefined to disable. */
  getLeft: () => SwipeRowAction | undefined;
  /** The swipe surface element, used to measure the exit distance. */
  getSurfaceRef: () => HTMLElement | undefined;
  /** Return false to ignore a pointer down (e.g. row is in an inert state). */
  canStart?: () => boolean;
}

/**
 * Per-row swipe-to-action gesture, the canonical Stockpilot standard for
 * operational lists. Mirrors the recognizer physics used everywhere else
 * (deadzone, axis-lock to X, rubber-band, rising-edge haptic at the commit
 * threshold) and honours `prefers-reduced-motion` by snapping instead of
 * sliding. Pointers that begin on a button / link / `[data-no-row-swipe]`
 * are ignored so the existing tap targets and keyboard a11y stay intact.
 */
export function useSwipeRow(options: SwipeRowOptions) {
  const [tx, setTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [isAnimating, setIsAnimating] = createSignal(false);
  const prefersReducedMotion = createPrefersReducedMotion();

  let hasSwiped = false;

  const settle = (delay: number) => {
    if (prefersReducedMotion()) {
      setIsAnimating(false);
      return;
    }
    setTimeout(() => setIsAnimating(false), delay);
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    commitPx: COMMIT_THRESHOLD_PX,
    haptics: true,
    hapticMs: HAPTIC_MS,
    canStart: ({ target }) => {
      if (options.canStart && !options.canStart()) {
        return false;
      }
      return !closestSelector(target, 'button, a, [data-no-row-swipe="true"]');
    },
    onStart: () => {
      setIsSwiping(true);
      setIsAnimating(false);
    },
    onMove: ({ dx: rawDx }) => {
      let dx = rawDx;
      // Clamp to directions that actually have an action wired.
      if (dx > 0 && !options.getRight()) {
        dx = 0;
      } else if (dx < 0 && !options.getLeft()) {
        dx = 0;
      }
      setTx(applyRubberBand(dx, RUBBER_BAND_LIMIT, RUBBER_BAND_FACTOR));
    },
    onEnd: () => {
      hasSwiped = true;
      setIsSwiping(false);
      setIsAnimating(true);

      const finalTx = tx();
      const width = options.getSurfaceRef()?.clientWidth ?? FALLBACK_WIDTH_PX;
      const right = options.getRight();
      const left = options.getLeft();

      if (finalTx > COMMIT_THRESHOLD_PX && right) {
        commit(right.onCommit, right.exits ? width : 0);
      } else if (finalTx < -COMMIT_THRESHOLD_PX && left) {
        commit(left.onCommit, left.exits ? -width : 0);
      } else {
        setTx(0);
        settle(CANCEL_ANIMATION_MS);
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsAnimating(true);
      setTx(0);
      settle(CANCEL_ANIMATION_MS);
    },
  });

  function commit(action: () => void, exitTx: number) {
    if (prefersReducedMotion()) {
      setTx(0);
      setIsAnimating(false);
      action();
      return;
    }
    setTx(exitTx);
    setTimeout(() => {
      action();
      setTx(0);
      setIsAnimating(false);
    }, COMMIT_ANIMATION_MS);
  }

  const base = pointerHandlers(recognizer);

  return {
    tx,
    isSwiping,
    isAnimating,
    commitThresholdPx: COMMIT_THRESHOLD_PX,
    handlePointerDown: base.onPointerDown,
    handlePointerMove: base.onPointerMove,
    handlePointerUp: base.onPointerUp,
    handlePointerCancel: base.onPointerCancel,
    /** True if the just-finished pointer sequence was a swipe (suppress click). */
    getHasSwiped: () => hasSwiped,
    resetHasSwiped: () => {
      hasSwiped = false;
    },
  };
}
